/**
 * What the message area at the foot of the sidebar is showing, as data.
 *
 * Kept apart from the markup because the interesting behaviour is not the drawing. A
 * message list has to decide what happens when the same thing goes wrong twice, and what
 * happens when a great many things go wrong at once, and both of those are worth being
 * sure about rather than discovering in a browser.
 *
 * These replaced modal dialogs, which is what shapes the rules. A dialog interrupts and is
 * dismissed by acknowledging it, so it can only ever show one thing and the question of a
 * second never arises. A message that sits quietly in a panel is not acknowledged, so it
 * has to answer both questions itself:
 *
 * - **The same message twice is one message.** Pressing a button that fails, and pressing
 *   it again, is one problem reported once. A second identical entry would say only that
 *   the button had been pressed twice, which the person pressing it already knows.
 * - **There is a limit.** Dropping a folder of unreadable files would otherwise fill the
 *   panel with a message per file and push everything else off the screen.
 *
 * - **A message goes on its own.** It is dismissed, or pushed out by the limit, or its time
 *   runs out. Left up until acknowledged, a panel of reports of things that have already
 *   happened becomes a panel of things somebody has to tidy away before the sidebar is
 *   theirs again, which is a chore charged for having been told something.
 *
 * How long it stays is [`dwellFor`], and the one thing that must not happen is a message
 * disappearing out from under somebody reading it. Half of that is here — the dwell is
 * scaled to how much there is to read — and the other half is the board's, which stops the
 * clock while the panel is under the pointer or holds the focus.
 */

export type NoticeLevel = 'error' | 'warning' | 'info';

/** A message as it is asked for. */
export interface NoticeInput {
  readonly level: NoticeLevel;
  /** A short heading. Omitted where the message says everything on its own. */
  readonly title?: string | undefined;
  readonly message: string;
  /** The part that explains rather than reports: what it means, or what to do. */
  readonly detail?: string | undefined;
}

/** A message as it is being shown, with the identity the board needs to track it. */
export interface Notice extends NoticeInput {
  readonly id: string;
}

/**
 * How many are shown at once.
 *
 * Four fills a reasonable amount of the panel's foot without crowding out the library
 * above it. Beyond that the oldest goes: a person reading a list of failures reads the
 * most recent first, and the very first of twenty is the least likely to still matter.
 */
export const MAX_NOTICES = 4;

/**
 * How long a word is worth, in milliseconds, when working out how long to leave a message.
 *
 * Around 170 words a minute, which is slower than silent reading on purpose. Nobody is
 * waiting for these: the message has to be noticed at the edge of the eye before it can be
 * read at all, and reading it means looking away from whatever prompted it.
 */
const READING_MS_PER_WORD = 350;

/**
 * The shortest and longest a message is left up, in milliseconds.
 *
 * The floor is what "a few seconds" has to mean for something as short as `Shared 3
 * models`, which by the words alone would be gone before it was seen. The ceiling is there
 * because a message that outstays this is not being read — it is being ignored, and the
 * panel is better off without it.
 */
const MIN_DWELL_MS = 4000;
const MAX_DWELL_MS = 12000;

/**
 * How long to leave a message up before it takes itself away.
 *
 * Scaled to how much there is to read rather than fixed, because these are not all the same
 * size: `Shared 3 models` is three words, and a file that would not parse arrives with a
 * heading, a report and an explanation of what to do about it. One timeout long enough for
 * the longest is a timeout that leaves the shortest sitting there being read twice, and one
 * short enough for the shortest cuts the longest off mid-sentence.
 */
export function dwellFor(notice: NoticeInput): number {
  const words = [notice.title, notice.message, notice.detail]
    .filter((part) => part !== undefined)
    .join(' ')
    .split(/\s+/u)
    .filter((word) => word.length > 0).length;

  return Math.min(MAX_DWELL_MS, Math.max(MIN_DWELL_MS, words * READING_MS_PER_WORD));
}

export class NoticeList {
  private items: Notice[] = [];
  private nextId = 1;

  /** Everything currently showing, oldest first. */
  get notices(): readonly Notice[] {
    return this.items;
  }

  /**
   * Show a message, or recognise one already showing.
   *
   * Returns the notice either way, so a caller can tell what it ended up with. An entry
   * matched as a repeat keeps its place rather than moving to the end: it is the same
   * message it was, and shuffling the list under somebody's eyes to say "again" would cost
   * them their place in it.
   */
  add(input: NoticeInput): Notice {
    const existing = this.items.find((item) => isSameMessage(item, input));
    if (existing) return existing;

    const notice: Notice = { ...input, id: `notice-${this.nextId.toString()}` };
    this.nextId += 1;

    this.items = [...this.items, notice].slice(-MAX_NOTICES);
    return notice;
  }

  /** Take one away. Dismissing something already gone is not an error. */
  dismiss(id: string): void {
    this.items = this.items.filter((item) => item.id !== id);
  }

  clear(): void {
    this.items = [];
  }
}

/**
 * Whether two messages say the same thing.
 *
 * Every field, not just the text: the same words at a different level are a different
 * report, and one carrying detail is not the one that carried none.
 */
function isSameMessage(notice: Notice, input: NoticeInput): boolean {
  return (
    notice.level === input.level &&
    notice.title === input.title &&
    notice.message === input.message &&
    notice.detail === input.detail
  );
}
